<template>
    <div class="w-full max-w-9xl mx-auto">
        <!-- Header Section -->
        <div class="w-full mb-6 md:mb-8">
            <!-- Mobile Header -->
            <div class="md:hidden">
                <h1 class="text-slate-800 dark:text-slate-100 text-xl font-semibold leading-7">
                    My best cards by category
                </h1>
                <p class="mt-1 text-slate-500 text-sm font-normal leading-5">
                    Cards from your wallet that provide the best rewards for
                    spending categories.
                </p>
            </div>
            <!-- Desktop Header -->
            <div
                class="hidden md:flex flex-col md:flex-row md:items-start md:justify-between"
            >
                <div class="flex-1">
                    <h1 class="text-slate-800 dark:text-slate-100 text-2xl font-semibold leading-8">
                        My best cards by category
                    </h1>
                    <p
                        class="mt-2 text-slate-500 text-sm font-normal leading-5"
                    >
                        Cards from your wallet that provide the best rewards for
                        spending categories.
                    </p>
                </div>
            </div>
        </div>

        <!-- ============================================ -->
        <!-- PAYWALL VIEW (non-subscribed users)          -->
        <!-- ============================================ -->
        <template v-if="!isSubscribed">
            <!-- Unlock Banner -->
            <div
                v-if="!bannerDismissed"
                class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 mb-6 rounded-lg border border-orange-200 bg-orange-50"
            >
                <div class="flex items-center gap-3">
                    <div
                        class="shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center"
                    >
                        <Icon
                            icon="credit-card"
                            class="w-5 h-5 text-orange-600"
                        />
                    </div>
                    <div>
                        <p
                            class="text-slate-800 dark:text-slate-100 text-sm font-semibold leading-5"
                        >
                            Unlock unlimited best cards by category
                        </p>
                        <p class="text-slate-500 text-sm font-normal leading-5">
                            Upgrade to 10xTravel+ to maximize the rewards on
                            your spending.
                        </p>
                    </div>
                </div>
                <div class="flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        class="text-slate-600 text-sm font-medium hover:text-slate-800 dark:text-slate-100 transition-colors"
                        @click="bannerDismissed = true"
                    >
                        Dismiss
                    </button>
                    <button
                        type="button"
                        class="px-4 py-2 rounded bg-[#FD421D] text-white text-sm font-medium hover:bg-[#e53b19] transition-colors shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
                        @click="showUpgradeModal = true"
                    >
                        Upgrade
                    </button>
                </div>
            </div>

            <!-- Category Filter (for paywall view) -->
            <div class="hidden md:flex items-center gap-4 mb-6">
                <ButtonPopover
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                    :closeOnSelect="false"
                >
                    <template #button-content>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Category
                        </span>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                        />
                    </template>
                    <template #default>
                        <ul class="max-h-[400px] overflow-y-auto p-1">
                            <li
                                v-for="cat in availableCategories"
                                :key="'paywall-cat-' + cat.key"
                            >
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-500 transition-colors flex items-center gap-3"
                                    @click="toggleCategory(cat.key)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange pointer-events-none"
                                        :checked="
                                            selectedCategories.includes(cat.key)
                                        "
                                        readonly
                                    />
                                    <Icon
                                        :icon="cat.icon || 'credit-card'"
                                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                    />
                                    <span>{{ cat.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedCategories.length > 0">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-orange-600 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-500 transition-colors"
                                    @click="resetCategoryFilter"
                                >
                                    Reset All
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
            </div>

            <!-- Paywall Cards Grid (Desktop) -->
            <div
                class="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            >
                <div
                    v-for="cat in filteredPaywallCategories"
                    :key="'paywall-desktop-' + cat.key"
                    class="bg-white dark:bg-slate-800 rounded-xl border border-transparent py-3 px-4"
                >
                    <div class="pb-3 flex items-center gap-2">
                        <Icon
                            :icon="cat.icon || 'credit-card'"
                            class="w-5 h-5 text-slate-500 dark:text-slate-400"
                        />
                        <span
                            class="text-sm font-medium text-slate-500 dark:text-slate-400 leading-5"
                        >
                            {{ cat.name }}
                        </span>
                    </div>
                    <div class="relative mb-4 mx-auto w-full max-w-[320px]">
                        <div
                            class="aspect-[1.586/1] rounded-lg overflow-hidden bg-gradient-to-br from-slate-700 to-slate-500 flex items-center justify-center"
                        >
                            <img
                                src="../../../../images/card-placeholder-blue.png"
                                alt="Card placeholder"
                                class="w-full h-full object-cover opacity-40"
                            />
                        </div>
                    </div>
                    <div class="flex items-center justify-between mt-auto">
                        <button
                            type="button"
                            class="text-[#FD421D] text-sm font-medium hover:text-[#e53b19] transition-colors cursor-pointer"
                            @click="showUpgradeModal = true"
                        >
                            Upgrade to view
                        </button>
                        <Icon
                            icon="chevron-right"
                            class="w-5 h-5 text-slate-400"
                        />
                    </div>
                </div>
            </div>

            <!-- Paywall Cards List (Mobile) -->
            <div class="md:hidden grid grid-cols-1 gap-4 mb-8">
                <div
                    v-for="cat in filteredPaywallCategories"
                    :key="'paywall-mobile-' + cat.key"
                    class="flex items-start gap-4 p-4 bg-white dark:bg-slate-800 rounded-lg shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
                >
                    <div
                        class="w-[76px] h-12 shrink-0 rounded overflow-hidden bg-gradient-to-br from-slate-700 to-slate-500 flex items-center justify-center"
                    >
                        <Icon
                            icon="credit-card"
                            class="w-6 h-6 text-slate-300 opacity-40"
                        />
                    </div>
                    <div class="flex flex-col items-start flex-1">
                        <div class="flex items-start gap-2 pb-1">
                            <Icon
                                :icon="cat.icon || 'credit-card'"
                                class="w-5 h-5 text-slate-500 dark:text-slate-400 shrink-0"
                            />
                            <span
                                class="text-slate-500 dark:text-slate-400 text-sm font-medium leading-5"
                            >
                                {{ cat.name }}
                            </span>
                        </div>
                        <button
                            type="button"
                            class="text-[#FD421D] text-sm font-medium hover:text-[#e53b19] transition-colors cursor-pointer"
                            @click="showUpgradeModal = true"
                        >
                            Upgrade to view
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <!-- ============================================ -->
        <!-- SUBSCRIBED VIEW (existing functionality)     -->
        <!-- ============================================ -->
        <template v-else>
            <!-- Filters -->
            <div
                v-if="hasCardsInWallet"
                class="hidden md:flex items-center gap-4 mb-8 md:mb-6"
            >
                <!-- User Filter (if multiple users) -->
                <div v-if="walletUsers.length > 1" class="flex items-center">
                    <ButtonPopover
                        buttonClass="btn-base btn-white !pl-3 !pr-2 h-10"
                        popoverClass="inline-block bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm dark:shadow-slate-900/50 z-30 p-0"
                    >
                        <template #button-content>
                            <Icon icon="user" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium truncate max-w-[200px]"
                            >
                                {{ activeUserName }}
                            </span>
                            <Icon
                                icon="chevron-grabber-vertical"
                                class="w-5 h-5 text-slate-600 dark:text-slate-300"
                            />
                        </template>
                        <template #default="{ closePopover }">
                            <ul class="inline-block min-w-[168px] w-max p-2">
                                <li
                                    v-for="user in walletUsers"
                                    :key="'switch-user-' + user.id"
                                    class="block w-full"
                                >
                                    <button
                                        type="button"
                                        @click="
                                            selectUser(user.id);
                                            closePopover();
                                        "
                                        class="w-full text-left p-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-10x-orange flex items-center gap-2 text-slate-800 dark:text-slate-100 whitespace-nowrap cursor-pointer rounded"
                                    >
                                        <Icon
                                            icon="user"
                                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                        />
                                        <span>{{ user.name }}</span>
                                    </button>
                                </li>
                            </ul>
                        </template>
                    </ButtonPopover>
                </div>

                <!-- Vertical Divider -->
                <div
                    v-if="walletUsers.length > 1"
                    class="w-px h-8 bg-slate-300 dark:bg-slate-600"
                ></div>

                <!-- Category Filter -->
                <ButtonPopover
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                    :closeOnSelect="false"
                >
                    <template #button-content>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Category
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedCategories.length > 0"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedCategories.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                        />
                    </template>
                    <template #default>
                        <ul class="max-h-[400px] overflow-y-auto p-1">
                            <li
                                v-for="cat in availableCategories"
                                :key="cat.key"
                            >
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-500 transition-colors flex items-center gap-3"
                                    @click="toggleCategory(cat.key)"
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange pointer-events-none"
                                        :checked="
                                            selectedCategories.includes(cat.key)
                                        "
                                        readonly
                                    />
                                    <Icon
                                        :icon="cat.icon || 'credit-card'"
                                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                    />
                                    <span>{{ cat.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedCategories.length > 0">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-orange-600 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-500 transition-colors"
                                    @click="resetCategoryFilter"
                                >
                                    Reset All
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>

                <!-- Reset all button - only shown when filters are active -->
                <button
                    v-if="hasActiveFilters"
                    class="btn-base btn-transparent !pl-4 !pr-3 !py-1.5"
                    @click="clearAllFilters"
                >
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                        Reset all
                    </span>
                    <Icon icon="x" class="w-3 h-3 text-slate-800 dark:text-slate-100" />
                </button>
            </div>

            <!-- Mobile Filters (single line with pills) -->
            <div
                v-if="hasCardsInWallet"
                class="md:hidden flex flex-col xs:flex-row xs:items-center gap-2 mb-6"
            >
                <!-- User Filter Pill (if multiple users) -->
                <button
                    v-if="walletUsers.length > 1"
                    type="button"
                    @click="openUserOverlay"
                    class="flex items-center gap-2 px-3 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:shadow-slate-900/50 xs:justify-start justify-between"
                >
                    <span class="flex gap-2">
                        <Icon
                            icon="user"
                            class="w-5 h-5 text-slate-800 dark:text-slate-100 shrink-0"
                        />
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 truncate max-w-[150px]"
                        >
                            {{ activeUserName }}
                        </span>
                    </span>
                    <Icon
                        icon="chevron-grabber-vertical"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100 shrink-0"
                    />
                </button>

                <!-- Category Filter Pill -->
                <button
                    type="button"
                    @click="showMobileCategoryOverlay = true"
                    class="flex items-center gap-2 px-3 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] dark:shadow-slate-900/50 xs:justify-start justify-between"
                >
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                        Category
                    </span>
                    <Transition name="fade">
                        <span
                            v-if="selectedCategories.length > 0"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedCategories.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                    />
                </button>
            </div>

            <!-- Mobile User Overlay (Bottom Sheet) -->
            <Transition name="fade">
                <div
                    v-if="showMobileUserOverlay"
                    class="md:hidden fixed inset-0 z-50"
                >
                    <div
                        class="absolute inset-0 bg-black/40"
                        @click="showMobileUserOverlay = false"
                    ></div>
                    <!-- Bottom Sheet Container -->
                    <div
                        v-click-outside="() => (showMobileUserOverlay = false)"
                        class="absolute left-0 right-0 bottom-0 flex flex-col justify-center items-start rounded-t-[16px] bg-white dark:bg-slate-800 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)]"
                    >
                        <!-- Header -->
                        <div
                            class="relative w-full flex flex-col items-start gap-2 pt-6 pr-[72px] pb-4 pl-6 rounded-t-[16px]"
                        >
                            <h3
                                class="self-stretch text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                            >
                                Show best cards for
                            </h3>
                            <!-- Close Button -->
                            <button
                                class="absolute right-4 bottom-3 flex p-2 items-start gap-2 rounded"
                                @click="showMobileUserOverlay = false"
                            >
                                <Icon icon="x" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>

                        <!-- User Options Container -->
                        <div
                            class="flex flex-col items-start self-stretch px-6 pb-4"
                        >
                            <!-- User Options (Radio buttons) -->
                            <button
                                v-for="user in walletUsers"
                                :key="'overlay-user-' + user.id"
                                type="button"
                                class="flex items-center py-3 w-full"
                                @click="tempSelectedUser = user.id"
                            >
                                <div class="flex items-center gap-2">
                                    <!-- Radio Circle -->
                                    <div
                                        class="flex items-center gap-2.5 py-0.5"
                                    >
                                        <div
                                            v-if="tempSelectedUser === user.id"
                                            class="w-4 h-4 rounded-full bg-10x-orange flex items-center justify-center"
                                        >
                                            <div
                                                class="w-1.5 h-1.5 rounded-full bg-white dark:bg-slate-800"
                                            ></div>
                                        </div>
                                        <div
                                            v-else
                                            class="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-500"
                                        ></div>
                                    </div>
                                    <!-- User Name -->
                                    <span
                                        class="text-slate-800 dark:text-slate-100 text-base font-normal leading-6"
                                    >
                                        {{ user.name }}
                                    </span>
                                </div>
                            </button>
                        </div>

                        <!-- Footer Actions -->
                        <div
                            class="flex justify-center items-end gap-2 self-stretch px-6 pt-4 pb-12"
                        >
                            <button
                                type="button"
                                class="flex-1 flex justify-center items-center gap-2 py-2.5 px-4 rounded bg-slate-800 dark:bg-slate-100 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
                                @click="applyUserSelection"
                            >
                                <span
                                    class="text-white dark:text-slate-900 text-sm font-medium leading-5"
                                >
                                    Apply
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>

            <!-- Mobile Category Overlay (Bottom Sheet) -->
            <Teleport to="body">
                <Transition name="fade">
                    <div
                        v-if="showMobileCategoryOverlay"
                        class="md:hidden fixed inset-0 z-[100]"
                    >
                        <div
                            class="absolute inset-0 bg-black/40"
                            @click="showMobileCategoryOverlay = false"
                        ></div>
                        <div
                            v-click-outside="
                                () => (showMobileCategoryOverlay = false)
                            "
                            class="absolute left-0 right-0 bottom-0 max-h-[85vh] rounded-t-[16px] bg-white dark:bg-slate-800 flex flex-col shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)]"
                        >
                            <!-- Header -->
                            <div
                                class="flex items-center justify-between px-4 py-4"
                            >
                                <h2
                                    class="text-slate-800 dark:text-slate-100 text-xl font-semibold"
                                >
                                    Filter
                                </h2>
                                <button
                                    class="w-10 h-10 flex items-center justify-center rounded-lg active:bg-slate-100 dark:active:bg-slate-700"
                                    @click="showMobileCategoryOverlay = false"
                                >
                                    <Icon
                                        icon="x"
                                        class="w-5 h-5 text-slate-500 dark:text-slate-400"
                                    />
                                </button>
                            </div>

                            <!-- Content -->
                            <div class="flex-1 overflow-y-auto px-4">
                                <!-- Category Section -->
                                <h3
                                    class="text-slate-800 dark:text-slate-100 text-base font-semibold mb-4"
                                >
                                    Category
                                </h3>

                                <div class="space-y-2">
                                    <button
                                        v-for="cat in availableCategories"
                                        :key="'overlay-cat-' + cat.key"
                                        type="button"
                                        class="w-full flex items-center gap-3 py-3 min-h-[44px] text-left"
                                        @click="toggleCategory(cat.key)"
                                    >
                                        <input
                                            type="checkbox"
                                            class="rounded border-slate-300 dark:border-slate-500 text-10x-orange w-5 h-5 focus:ring-0 pointer-events-none"
                                            :checked="
                                                selectedCategories.includes(
                                                    cat.key
                                                )
                                            "
                                            readonly
                                        />
                                        <Icon
                                            :icon="cat.icon || 'credit-card'"
                                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                        />
                                        <span
                                            class="text-slate-800 dark:text-slate-100 text-base font-normal"
                                        >
                                            {{ cat.name }}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <!-- Footer -->
                            <div class="px-4 pt-4 pb-12 flex gap-3">
                                <button
                                    type="button"
                                    class="flex-1 h-11 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
                                    @click="
                                        resetCategoryFilter();
                                        showMobileCategoryOverlay = false;
                                    "
                                >
                                    Clear all
                                </button>
                                <button
                                    type="button"
                                    class="flex-1 h-11 rounded bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium"
                                    @click="showMobileCategoryOverlay = false"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </Transition>
            </Teleport>

            <!-- Mobile Cards List (separated cards with 12px gap, uniform height) -->
            <div
                v-if="filteredCards.length > 0"
                class="md:hidden grid grid-cols-1 gap-4 mb-8 auto-rows-fr"
            >
                <CategoryCard
                    v-for="item in filteredCards"
                    :key="'mobile-' + item.category_key"
                    :category="item"
                    @click="openCardModal"
                />
            </div>

            <!-- Desktop Cards Grid -->
            <div
                v-if="filteredCards.length > 0"
                class="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            >
                <CategoryCard
                    v-for="item in filteredCards"
                    :key="item.category_key"
                    :category="item"
                    @click="openCardModal"
                />
            </div>

            <!-- Empty State: No matching cards (filters active) -->
            <div
                v-else-if="hasActiveFilters"
                class="flex flex-col items-center justify-center min-h-[40vh] md:min-h-[60vh]"
            >
                <div
                    class="w-16 h-16 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700"
                >
                    <Icon
                        icon="filters"
                        :width="32"
                        :height="32"
                        class="text-slate-500 dark:text-slate-400"
                    />
                </div>

                <div class="flex flex-col items-center pt-6 gap-2">
                    <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                        No matching cards
                    </h3>
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                    >
                        Try removing some filters to see best cards.
                    </p>
                </div>

                <div class="flex flex-col items-start pt-6">
                    <button
                        @click="clearAllFilters"
                        class="flex items-start gap-2 px-4 py-2 rounded border border-slate-200 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 shadow-sm dark:shadow-slate-900/50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
                    >
                        Clear filters
                    </button>
                </div>
            </div>

            <!-- Empty State: No cards in wallet at all -->
            <div
                v-else-if="localWalletCardCount === 0"
                class="flex flex-col items-center justify-center min-h-[40vh] md:min-h-[60vh]"
            >
                <div
                    class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center"
                >
                    <Icon
                        icon="card-check"
                        :width="32"
                        :height="32"
                        class="text-slate-500 dark:text-slate-400"
                    />
                </div>

                <div class="flex flex-col items-center pt-6 gap-2">
                    <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                        No cards added yet
                    </h3>
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                    >
                        Add cards to see top earners by category.
                    </p>
                </div>

                <div class="flex flex-col items-start pt-6">
                    <Link
                        href="/wallet"
                        class="flex items-start gap-2 px-4 py-2 rounded border border-slate-200 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 shadow-sm dark:shadow-slate-900/50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
                    >
                        View My Wallet
                    </Link>
                </div>
            </div>

            <!-- Empty State: Selected user has no cards in this wallet -->
            <div
                v-else-if="!localHasCardsForUser"
                class="flex flex-col items-center justify-center min-h-[40vh] md:min-h-[60vh]"
            >
                <div
                    class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center"
                >
                    <Icon
                        icon="card-check"
                        :width="32"
                        :height="32"
                        class="text-slate-500 dark:text-slate-400"
                    />
                </div>

                <div class="flex flex-col items-center pt-6 gap-2">
                    <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                        No cards for {{ localSelectedUserName || 'this user' }}
                    </h3>
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                    >
                        This user has no cards in the shared wallet yet.
                    </p>
                </div>
            </div>

            <!-- Empty State: User has cards but none have usable earn-rate data -->
            <div
                v-else
                class="flex flex-col items-center justify-center min-h-[40vh] md:min-h-[60vh]"
            >
                <div
                    class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center"
                >
                    <Icon
                        icon="card-check"
                        :width="32"
                        :height="32"
                        class="text-slate-500 dark:text-slate-400"
                    />
                </div>

                <div class="flex flex-col items-center pt-6 gap-2">
                    <h3 class="text-slate-800 dark:text-slate-100 text-lg font-medium leading-7">
                        No best-card recommendations for
                        {{ localSelectedUserName || 'this user' }} yet
                    </h3>
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                    >
                        Their cards are missing earn-rate data — this can be
                        filled in from the wallet.
                    </p>
                </div>

                <div class="flex flex-col items-start pt-6">
                    <Link
                        href="/wallet"
                        class="flex items-start gap-2 px-4 py-2 rounded border border-slate-200 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 shadow-sm dark:shadow-slate-900/50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
                    >
                        View My Wallet
                    </Link>
                </div>
            </div>

            <!-- Card Info Modal -->
            <CardInfoModal
                :isOpen="isModalOpen"
                :card="selectedCardData?.best_card"
                :category="
                    selectedCardData
                        ? {
                              name: selectedCardData.category_name,
                              icon: selectedCardData.category_icon,
                          }
                        : null
                "
                :otherCards="selectedCardData?.other_cards ?? []"
                @close="closeCardModal"
                @view-in-wallet="navigateToWallet"
                @view-other-in-wallet="navigateToCardInWallet"
                @why-this-card="handleWhyThisCard"
            />
        </template>

        <!-- Upgrade Modal (shared across both views) -->
        <UpgradeModal
            :show="showUpgradeModal"
            description="This feature is part of the 10xTravel+ plan. Upgrade to unlock it and get full access."
            @close="showUpgradeModal = false"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import axios from 'axios';
import Icon from '@/components/ui/icon/icon.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import CategoryCard from './category-card.vue';
import CardInfoModal from './card-info-modal.vue';
import UpgradeModal from '@/modals/upgrade-modal.vue';

const props = defineProps({
    bestCards: {
        type: Array,
        default: () => [],
    },
    walletUsers: {
        type: Array,
        default: () => [],
    },
    availableCategories: {
        type: Array,
        default: () => [],
    },
    selectedUserId: {
        type: [Number, String],
        default: null,
    },
    walletCardCount: {
        type: Number,
        default: 0,
    },
    filteredCardCount: {
        type: Number,
        default: 0,
    },
    hasCardsForUser: {
        type: Boolean,
        default: false,
    },
    selectedUserName: {
        type: String,
        default: null,
    },
    isSubscribed: {
        type: Boolean,
        default: false,
    },
});

const showMobileCategoryOverlay = ref(false);
const showMobileUserOverlay = ref(false);
const showUpgradeModal = ref(false);
const bannerDismissed = ref(false);

// Initialize selected categories from URL params
const urlParams = new URLSearchParams(window.location.search);
const selectedCategories = ref(
    urlParams.get('categories') ? urlParams.get('categories').split(',') : []
);

const selectedUser = ref(
    props.selectedUserId
        ? Number(props.selectedUserId)
        : props.walletUsers.length > 0
          ? props.walletUsers[0]?.id
          : null
);
const tempSelectedUser = ref(null);
const isLoading = ref(false);
const localBestCards = ref(props.bestCards);
const localWalletCardCount = ref(props.walletCardCount);
const localHasCardsForUser = ref(props.hasCardsForUser);
const localSelectedUserName = ref(
    props.selectedUserName ||
        (props.walletUsers.find((u) => u.id === Number(props.selectedUserId))
            ?.name ??
            null)
);

const isModalOpen = ref(false);
const selectedCardData = ref(null);

const activeUserName = computed(() => {
    if (!selectedUser.value && props.walletUsers.length > 0) {
        return props.walletUsers[0]?.name || 'Select User';
    }
    const user = props.walletUsers.find((u) => u.id === selectedUser.value);
    return user?.name || 'Select User';
});

const hasActiveFilters = computed(() => {
    return selectedCategories.value.length > 0;
});

const hasCardsInWallet = computed(() => {
    return localBestCards.value.length > 0;
});

const filteredCards = computed(() => {
    let cards = localBestCards.value;

    if (selectedCategories.value.length > 0) {
        cards = cards.filter((c) =>
            selectedCategories.value.includes(c.category_key)
        );
    }

    return cards;
});

const filteredPaywallCategories = computed(() => {
    if (selectedCategories.value.length > 0) {
        return props.availableCategories.filter((cat) =>
            selectedCategories.value.includes(cat.key)
        );
    }
    return props.availableCategories;
});

const toggleCategory = (categoryKey) => {
    const index = selectedCategories.value.indexOf(categoryKey);
    if (index === -1) {
        selectedCategories.value = [...selectedCategories.value, categoryKey];
    } else {
        selectedCategories.value = selectedCategories.value.filter(
            (k) => k !== categoryKey
        );
    }
    updateURL();
};

const resetCategoryFilter = () => {
    selectedCategories.value = [];
    updateURL();
};

const clearAllFilters = () => {
    selectedCategories.value = [];
    updateURL();
};

// Update URL with current filter state
const updateURL = () => {
    const url = new URL(window.location);
    if (selectedCategories.value.length > 0) {
        url.searchParams.set('categories', selectedCategories.value.join(','));
    } else {
        url.searchParams.delete('categories');
    }
    window.history.replaceState({}, '', url);
};

const selectUser = async (userId) => {
    selectedUser.value = userId;

    // Reload data with user filter
    isLoading.value = true;
    try {
        const { data } = await axios.get('/api/wallets/best-cards', {
            params: { user_id: userId },
        });
        localBestCards.value = data.categories || [];
        localWalletCardCount.value = data.wallet_card_count ?? 0;
        localHasCardsForUser.value = data.has_cards_for_user ?? false;
        localSelectedUserName.value =
            data.selected_user_name ??
            props.walletUsers.find((u) => u.id === Number(userId))?.name ??
            null;
    } catch (error) {
        console.error('Failed to fetch best cards:', error);
    } finally {
        isLoading.value = false;
    }
};

const openUserOverlay = () => {
    tempSelectedUser.value = selectedUser.value;
    showMobileUserOverlay.value = true;
};

const applyUserSelection = () => {
    if (tempSelectedUser.value !== selectedUser.value) {
        selectUser(tempSelectedUser.value);
    }
    showMobileUserOverlay.value = false;
};

const openCardModal = (categoryData) => {
    selectedCardData.value = categoryData;
    isModalOpen.value = true;
};

const closeCardModal = () => {
    isModalOpen.value = false;
    selectedCardData.value = null;
};

const navigateToWallet = () => {
    navigateToCardInWallet(
        selectedCardData.value?.best_card?.connected_account_id
    );
};

const navigateToCardInWallet = (connectedAccountId) => {
    closeCardModal();
    if (connectedAccountId) {
        router.visit(`/wallet?card_id=${connectedAccountId}&open_sidebar=true`);
    } else {
        router.visit('/wallet');
    }
};

const handleWhyThisCard = () => {
    // TODO: Implement tooltip (ticket #3)
    console.log('Why this card clicked');
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
